import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Mail, Phone, MessageCircle, HelpCircle } from "lucide-react";

export default function HelpPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [search, setSearch] = useState("");

  const faqs = [
    {
      question: "How do I check-in a guest?",
      answer:
        "Click on the 'Check-In Guest' button on the dashboard, select the verification method, and complete the steps."
    },
    {
      question: "How do I top up my wallet?",
      answer:
        "Go to the Wallet page, click the 'Top Up Wallet' button, and follow the payment instructions."
    },
    {
      question: "What happens when a guest is checked out?",
      answer:
        "Once a guest is checked out, they must be re-verified before checking in again."
    },
    {
      question: "How can I contact support?",
      answer:
        "You can email us at support@hotelverify.com or start a live chat session."
    }
  ];

  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="d-flex vh-100 overflow-hidden">
      {/* ✅ Sidebar */}
      <Sidebar
        active="help"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* ✅ Main Content */}
      <div className="flex-grow-1 d-flex flex-column bg-light">
        <Header
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        <main className="flex-grow-1 overflow-auto p-4">
          {/* 🔍 Search Help Section */}
          <div className="bg-white rounded p-4 mb-4 text-center">
            <h3 className="fw-bold mb-2">How can we help you?</h3>
            <p className="text-muted mb-3">
              Search FAQs or contact our support team for assistance.
            </p>
            <div className="position-relative mx-auto" style={{ maxWidth: "400px" }}>
              <Search
                size={18}
                className="position-absolute top-50 start-2 translate-middle-y text-muted"
                style={{ left: "12px" }}
              />
              <input
                type="text"
                className="form-control ps-5 rounded-pill"
                placeholder="Search help articles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* 📂 Help Categories */}
          <div className="row g-3 mb-4">
            {[
              { title: "Account & Profile", icon: <HelpCircle size={28} />, desc: "Manage your account settings and profile." },
              { title: "Wallet & Billing", icon: <HelpCircle size={28} />, desc: "Learn how to top up and manage payments." },
              { title: "Verification Issues", icon: <HelpCircle size={28} />, desc: "Get help with guest verification problems." },
              { title: "Reports & Data", icon: <HelpCircle size={28} />, desc: "Understand how to generate and read reports." }
            ].map((cat, idx) => (
              <div className="col-12 col-md-6 col-lg-3" key={idx}>
                <div
                  className="border rounded p-3 h-100 text-center hover-shadow"
                  style={{ transition: "0.3s" }}
                >
                  <div className="mb-2 text-primary">{cat.icon}</div>
                  <h6 className="fw-bold">{cat.title}</h6>
                  <p className="small text-muted">{cat.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ❓ FAQ Section */}
          <div className="bg-white rounded p-4 mb-4">
            <h5 className="fw-bold mb-3">Frequently Asked Questions</h5>
            {faqs
              .filter(f => f.question.toLowerCase().includes(search.toLowerCase()))
              .map((faq, index) => (
              <div key={index} className="mb-3 border-bottom pb-2">
                <button
                  className="w-100 text-start fw-bold d-flex justify-content-between align-items-center"
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  style={{ background: "none", border: "none", padding: 0 }}
                >
                  {faq.question}
                  <span>{openFAQ === index ? "−" : "+"}</span>
                </button>
                {openFAQ === index && (
                  <p className="text-muted mt-2">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>

          {/* 📞 Contact Support Card */}
          <div className="bg-white rounded p-4 text-center">
            <h5 className="fw-bold mb-2">Still Need Help?</h5>
            <p className="text-muted mb-3">
              Our support team is here 24/7 to assist you.
            </p>
            <div className="d-flex justify-content-center gap-3 mb-3">
              <button className="btn btn-outline-primary rounded-pill d-flex align-items-center gap-2">
                <Mail size={18} /> Email Support
              </button>
              <button className="btn btn-outline-success rounded-pill d-flex align-items-center gap-2">
                <Phone size={18} /> Call Us
              </button>
              <button className="btn btn-primary rounded-pill d-flex align-items-center gap-2">
                <MessageCircle size={18} /> Live Chat
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
